// The answer-variant list of a heatmap: what a row stands for, and the open-ended block it
// expands to.
//
// A heatmap expands either the pins the respondents dropped or the clusters those pins were
// grouped into, depending on which stat is being read; both are drawn as answer variants, so they
// are normalized into that shape here. Each row is a question in its own right as far as the
// report params go -- it carries the key and id the retired sub-item view derived -- and the shell
// reads those params and hands them over.

import { OE_CHART_VIEW_MODES, OE_HEALS_VIEW_MODE } from '@aytm/app/stats/page/question-views/oe/lib/family'

// a cluster is addressed by its uid, which carries characters a report-param key cannot
export const clusterPosition = uid => String(uid).replace('@', '_').replace('.', '_')

// the key and id every report param a variant owns hangs off
export const answerVariantKey = (questionKey, position) => `${questionKey}.${position}`
export const answerVariantId = (questionId, position) => `${questionId}-${position}`

// The pins worth expanding: one with verbatims behind it that the chart is still showing.
const calcPinItems = (batches, config) =>
  (batches || []).filter(
    batch =>
      batch?.comments && (!Object.hasOwn(config || {}, 'batches') || config.batches.includes(`b${batch.position}`))
  )

// The clusters worth expanding, drawn as answer variants: a cluster is named by its title and
// addressed by its uid, and the verbatims behind it are counted on its stat.
const calcClusterItems = clusters =>
  (clusters || [])
    .filter(cluster => cluster?.uid && cluster.stat?.comments && !cluster.off)
    .map(cluster => ({
      ...cluster,
      position: clusterPosition(cluster.uid),
      text: cluster.title,
      comments: cluster.stat.comments
    }))

export const calcAnswerVariantItems = ({ statType, batches, clusters, config } = {}) => {
  if ('pins' === statType) return calcPinItems(batches, config)
  if ('clusters' === statType) return calcClusterItems(clusters)

  return []
}

// A cluster is read as the verbatims it collected; a pin reads the slice of the answer that is its
// own. A question still loading has neither yet, and neither has a row whose own fetch is out.
const answerAt = ({ statType, answer, item, loadingRows }) => {
  if ('loading' === answer || loadingRows?.has(String(item.position))) return 'loading'
  if ('clusters' === statType) return item

  return answer?.[`b${item.position}`] || { total: 0 }
}

// A cluster has no chart of its own to offer -- what it holds is a cloud of the words its
// verbatims are made of.
const variantFormatOf = ({ statType, format }) => {
  const own = format?.subitems || format || {}

  return 'clusters' === statType ? { ...own, viewMode: 'wordcloud' } : own
}

export const buildAnswerVariantProps =
  ({
    questionId,
    questionKey,
    type,
    statType,
    format,
    components,
    answer,
    modelDefaults,
    clickable,
    questionChartMode,
    defaultSortOrder,
    reportPercentDecimals,
    batch,
    link,
    isReportXpert,
    answerVariantParams = {},
    loadingRows,
    handlers = {}
  } = {}) =>
  item => {
    const position = item?.position
    if (null == position) return null

    const id = answerVariantId(questionId, position)
    const key = answerVariantKey(questionKey, position)

    return {
      id,
      parentQuestionId: questionId,
      questionKey: key,
      type,
      position,
      // a variant draws under the question's sub-item settings where it has them
      format: variantFormatOf({ statType, format }),
      components: components?.subitems || components,
      answer: answerAt({ statType, answer, item, loadingRows }),
      answerVariant: item,
      clickable,
      questionChartMode,
      defaultSortOrder,
      reportPercentDecimals,
      chartViewModes: OE_CHART_VIEW_MODES,
      healViewMode: OE_HEALS_VIEW_MODE,
      modelDefaults,
      params: answerVariantParams[position] || {},
      labelTarget: { id, questionId, batch },
      commentsTarget: { id, questionId, link, batch, isReportXpert },
      // The shell holds one block per variant, so anything it answers on a block's behalf is told
      // which one asked: an exported image is named after the variant rather than the question it
      // hangs on, and a clicked label is resolved by the variant's own block.
      handlers: {
        ...handlers,
        onWcExport: (params, data, callback) => handlers.onWcExport?.({ ...params, position: key }, data, callback),
        onExport: (params, callback) => handlers.onExport?.(params, callback, key),
        onLabelClick: (event, position, answerVariants) => handlers.onLabelClick?.(event, position, answerVariants, key)
      },
      // the identity travels with the api: the exporters read a variant as a question of its own,
      // and there is no model of its own left to read it off
      onReady: api => handlers.onAnswerVariantReady?.(position, api, { id, key, type, position, uid: item.uid, item }),
      onStateChange: (reason, options) => handlers.onAnswerVariantStateChange?.(position, key, reason, options),
      onWcParamsChange: params => handlers.onAnswerVariantWcParamsChange?.(id, params),
      onOutdatedPreset: () => handlers.onAnswerVariantStateChange?.(position, key, 'state', {})
    }
  }
